import { useRef, useState } from 'react'

import { IonContent, IonPage } from '@ionic/react'

import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import UserSignupViewComponent from '../../components/user/views/sign-up/UserSignupViewComponent'
import { CONST } from '../../const/const'
import { UserModel } from '../../models/users/user.model'
import FirebaseApp from '../../services/firebaseApp'
import firestoreService from '../../services/firestoreService'
import UserService from '../../services/firebase-service/users/userService'
import { useAppDispatch } from '../../store/hooks'
import {
  setIsLoggedIn,
  setUserId,
  setUserProfileComplete,
  setProfileDetails,
  storeIsFirstTime,
  storeUserId,
} from '../../store/user/userSlice'
import { storeDisplayName } from '../../store/user/userThunk'

// Redux
import useToasterHelper from '../../helpers/ToasterHelper'
import { authHelpers } from '../../helpers/helpers'
import { getConvertedData } from '../../models/interface.helper'
import { httpService } from '../../services/httpService'
import { storeUseMode } from '../../store/system/systemThunk'
import { USE_MODE_CONST } from '../../const/use-mode'
import { useCreate } from '../../hooks/useCreate'
import useIntercomUpdate from '../../hooks/useIntercomUpdate'

const SignUpPage = () => {
  // Hooks and vars
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const { getFirebaseErrors } = useCreate()
  const { updateIntercomUser } = useIntercomUpdate()

  const processStatus = useRef<'running' | null>(null)

  const [loading, setLoading] = useState(false)

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * Document this
   */
  const onSignUpButtonPressedHandler = async (data: any, userType: string) => {
    const responseError = await authHelpers.isUsernameUnique(data.username)

    if (responseError) {
      setLoading(false)
      processStatus.current = null
      toastFunctions.error({ message: responseError })
      return null
    }
    try {
      if (processStatus.current === 'running') return 0

      processStatus.current = 'running'
      setLoading(true)

      const result = await createUserWithEmailAndPassword(
        FirebaseApp.auth,
        data?.email?.toLowerCase(),
        data?.newPassword
      )

      await updateProfile(result.user, {
        displayName: data?.fullname,
      })

      await httpService({
        url: `create_user`,
        method: 'POST',
        data: {
          uuid: result.user.uid,
          userName: data?.username ?? '',
          email: `${result.user.email}`.toLowerCase(),
          userType,
        },
      })

      dispatch(setUserId(result.user.uid))
      dispatch(storeIsFirstTime(true))
      dispatch(setUserProfileComplete(true))

      let user_data_doc = null
      let document_exists = false

      while (!document_exists) {
        user_data_doc = await UserService.getUserInfoById(result.user.uid)
        document_exists = user_data_doc.exists()
      }

      let userData = UserModel.fromFirestoreDoc(user_data_doc).toObject()

      if ('username' in data) {
        userData.userName = data?.username
      } else {
        userData.userName = data?.userName
        userData.userBusinessName = data?.userName
      }
      if ('website' in data) {
        userData.websiteUrl = data?.website
      }

      if ('profilePic' in data) {
        userData.userProfilePicture = data.profilePic
        userData.userPhoneNumber = data.userPhoneNumber
        userData.userCategory = data.userCategory
        userData.userAddress = data.userAddress
        userData.vendorname = data.vendorname
      }

      userData.userFullName = data?.fullname || ''

      if (!userData.userStripeId && data?.fullname && userData?.userEmail) {
        const customerData = await httpService({
          url: `create_stripe_customer`,
          method: 'POST',
          data: {
            userEmail: userData?.userEmail.toLowerCase(),
            userStripeAccountId: userData.userStripeAccountId,
          },
        })

        userData.userStripeId = customerData?.customer?.stripe?.id
      }

      if (userType) {
        userData.userType = userType
        if (userType === USE_MODE_CONST.COMPETITOR || userType === USE_MODE_CONST.STAFF) {
          let CompetitorSubRole = sessionStorage.getItem('competitorRoles')
          const competitorSubRole = CompetitorSubRole?.split(',')
          userData.userSubRoles = competitorSubRole || []
        }
      }

      if ('nationality' in data || 'userNationality' in data) {
        userData.userNationality = data.nationality.value
      }

      let userData_ = new UserModel(userData)

      await firestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
        result.user.uid,
        userData_.toObject()
      )

      // const [firstName, ...lastName] = data.fullname.split(' ')

      // const affiliateData = {
      //   email: data.email,
      //   first_name: firstName,
      //   last_name: lastName.join(' '),
      //   stripe_customer_id: data.userStripeId,
      // }

      try {
        // const result = await httpService({
        //   url: 'create_rewardful_affiliate',
        //   method: 'POST',
        //   data: affiliateData,
        // })
        // console.log('Affiliate created:', result.data)
      } catch (error) {
        console.error('Error creating affiliate:', error)
      }

      dispatch(setIsLoggedIn(true))
      dispatch(setProfileDetails(getConvertedData(userData_.toObject())))
      dispatch(storeUserId(result.user.uid))
      dispatch(storeDisplayName(data?.username))
      dispatch(storeUseMode(userType))

      // Update Intercom with the new user data
      await updateIntercomUser(userData_.toObject())

      setLoading(false)
      processStatus.current = null
    } catch (error: any) {
      processStatus.current = null
      console.error(error, 'error')
      toastFunctions.error({ message: getFirebaseErrors(error) })
    } finally {
      setLoading(false)
    }
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  return (
    <IonPage>
      <IonContent>
        <UserSignupViewComponent
          onSignUpButtonPressed={onSignUpButtonPressedHandler}
          loading={loading}
        />
      </IonContent>
    </IonPage>
  )
}

SignUpPage.isauthpage = true

export default SignUpPage
